import { createSlice } from '@reduxjs/toolkit'
import { API } from 'src/utils/interceptor'
import { setOneVenue as setOneVenueToAccount } from 'src/features/AuthSlice'
import { SuccessNotification } from 'src/notifications/SuccessNotification'
import Sentry from 'src/utils/sentry'
import { withTidyVenueTitle } from 'src/utils/venueName'

const venueSlice = createSlice({
  name: 'venue',
  initialState: {
    venue: null,
    isLoading: false,
    venueInfo: null,
  },
  reducers: {
    setVenue: (state, action) => {
      state.venue = withTidyVenueTitle(action.payload)
    },
    setVenueInfo: (state, action) => {
      state.venueInfo = action.payload
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setFeatureLimit: (state, action) => {
      const usageLimits = state.venue?.usageLimits
      if (!usageLimits) return
      if (!usageLimits.features) {
        usageLimits.features = {}
      }
      const features = usageLimits.features
      const featureKey = action.payload.feature
      if (!features[featureKey] || typeof features[featureKey] !== 'object') {
        features[featureKey] = { limit: action.payload.limit }
        return
      }
      features[featureKey].limit = action.payload.limit
    },
  },
})

export const { setVenue, setIsLoading, setVenueInfo, setFeatureLimit } =
  venueSlice.actions

const mapStockTakeEmailSettingsPayload = data => {
  const mapped = { ...data }
  const normalizeOptionalText = value => {
    if (value === undefined || value === null) return ''
    return String(value).trim()
  }

  if ('stockTakeAutoEmailEnabled' in mapped) {
    mapped.stockTakeAutoEmailEnabled = Boolean(mapped.stockTakeAutoEmailEnabled)
  }

  if ('stockTakeReportEmails' in mapped) {
    mapped.stockTakeReportEmails = Array.isArray(mapped.stockTakeReportEmails)
      ? mapped.stockTakeReportEmails
          .map(email => String(email || '').trim())
          .filter(Boolean)
      : []
  }

  if ('stockTakeDefaultSiteName' in mapped) {
    mapped.stockTakeDefaultSiteName = normalizeOptionalText(
      mapped.stockTakeDefaultSiteName,
    )
  }
  if ('stockTakeDefaultSiteManager' in mapped) {
    mapped.stockTakeDefaultSiteManager = normalizeOptionalText(
      mapped.stockTakeDefaultSiteManager,
    )
  }
  if ('stockTakeDefaultLineManager' in mapped) {
    mapped.stockTakeDefaultLineManager = normalizeOptionalText(
      mapped.stockTakeDefaultLineManager,
    )
  }
  if ('stockTakeDefaultCountedByName' in mapped) {
    mapped.stockTakeDefaultCountedByName = normalizeOptionalText(
      mapped.stockTakeDefaultCountedByName,
    )
  }
  if ('stockTakeDefaultReasonForCount' in mapped) {
    mapped.stockTakeDefaultReasonForCount = normalizeOptionalText(
      mapped.stockTakeDefaultReasonForCount,
    )
  }
  if ('stockTakeDefaultCountNotes' in mapped) {
    mapped.stockTakeDefaultCountNotes = normalizeOptionalText(
      mapped.stockTakeDefaultCountNotes,
    )
  }

  return mapped
}

export const editVenue = (data, venueId) => async dispatch => {
  try {
    // Note: gpReportEmails/gpReportFrequency, reconciliationReportEmails/
    // reconciliationReportFrequency, and stockTakeReportEmails/stockTakeAutoEmailEnabled
    // are supported fields and persisted via settings API.
    const payload = mapStockTakeEmailSettingsPayload(data)
    const venue = await API.post('api', `settings/${venueId}`, {
      body: payload,
    })

    dispatch(setVenue(venue))
    dispatch(setOneVenueToAccount(venue))
    SuccessNotification({
      message: 'Venue settings',
      description: 'The changes have been saved',
    })
    return venue
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const updateVenueCode = venueId => async dispatch => {
  try {
    const venue = await API.put('api', `settings/${venueId}/false`, {
      body: {},
    })
    dispatch(setVenue(venue))
    dispatch(setOneVenueToAccount(venue))
    SuccessNotification({
      message: 'Venue settings',
      description: 'The venue code have been updated',
    })
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const updateAdminVenueCode = venueId => async dispatch => {
  try {
    const venue = await API.put('api', `settings/${venueId}/true`, {
      body: {},
    })
    dispatch(setVenue(venue))
    dispatch(setOneVenueToAccount(venue))
    SuccessNotification({
      message: 'Venue settings',
      description: 'The admin venue code have been updated',
    })
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const getVenue = venueId => async dispatch => {
  try {
    dispatch(setIsLoading(true))
    const data = await API.get('api', `venue/${venueId}`)
    dispatch(setVenue(data))
    dispatch(setIsLoading(false))
  } catch (e) {
    dispatch(setIsLoading(false))
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const createVenue = data => async () => {
  try {
    await API.post('api', `venue`, {
      body: {
        title: data.title,
        username: data.username,
        TFRVenue: data.TFRVenue,
        JUSTVenue: data?.JUSTVenue,
        GREENVenue: data?.GREENVenue,
      },
    })
    window.location.reload()
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const deleteVenue = venueId => async () => {
  try {
    await API.del('api', `venue/${venueId}`)
    window.location.reload()
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const pushAppNotification =
  (message, venueIds = [], supplierIds = []) =>
  async () => {
    try {
      const response = await API.post('api', `pushAppNotification`, {
        body: { message, venueIds, supplierIds },
      })
      SuccessNotification({
        message: 'Push Notification',
        description: response.message,
      })
    } catch (error) {
      console.error(error)
      Sentry.captureException(error)
      return false
    }
  }

export const fetchVenueInfo = venueId => async dispatch => {
  try {
    const venueInfo = await API.get('api', `venueInfo/${venueId}?days=${30}`)
    dispatch(setVenueInfo(venueInfo))
  } catch (error) {
    console.error(error)
    Sentry.captureException(error)
    return false
  }
}

export const toggleSubscription = data => async dispatch => {
  try {
    dispatch(setIsLoading(true))
    if (data?.isSubscribed) {
      await API.post('api', `cancelSubscriptionByAdmin/${data?.venueId}`, {
        body: {
          plan: data?.plan,
        },
      })
    } else {
      if (data?.isAdmin) {
        await API.post('api', `giveSubscriptionByAdmin/${data?.venueId}`, {
          body: {
            plan: data?.plan,
          },
        })
      } else {
        await API.post('api', `requestSubscription/${data?.venueId}`, {
          body: {
            plan: data?.plan,
          },
        })
      }
    }
    dispatch(setIsLoading(false))
    window.location.reload()
  } catch (e) {
    dispatch(setIsLoading(false))
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const xeroIntegration = (venueId, contactEmail) => async dispatch => {
  try {
    await dispatch(updateXeroContactEmail(venueId, contactEmail))
    const { authorizationUrl } = await API.get(
      'api2',
      `venue/${venueId}/connectXero`,
    )

    //redirect to xero
    window.location.href = authorizationUrl
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const disConectXero = venueId => async () => {
  try {
    const response = await API.post('api2', `venue/disConnnectXero`, {
      body: { venueId },
    })
    if (response?.success) {
      return {
        success: true,
        message:
          response?.message ||
          'Xero has been disconnected successfully for this venue.',
      }
    }
    return { success: false, message: response?.message || 'Disconnect failed' }
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return {
      success: false,
      message:
        e?.response?.data?.message ||
        e?.message ||
        'Failed to disconnect Xero account',
    }
  }
}

export const updateXeroContactEmail = (venueId, contactEmail) => async () => {
  try {
    await API.post('api2', `venue/${venueId}/xero-contact-email`, {
      body: { contactEmail },
    })
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}
export default venueSlice
